import { useEffect, useMemo, useState } from "react";
import { fetchAdminContent, fetchContactSubmissions, loginAdmin, saveAdminContent } from "@/lib/contentApi";
import { CONTENT_VERSION, getEditableSeedContent, getLocalizedContentFromEditable, languages, normalizeEditableContent } from "@/data/trinityI18n";
import { AdminField, AdminListField, AdminSection } from "@/components/admin/AdminFormControls";

const editorTabs = [
  { id: "site", label: "Site/Nav" },
  { id: "hero", label: "Hero" },
  { id: "snapshot", label: "Snapshot" },
  { id: "lineCard", label: "Line Card" },
  { id: "milestones", label: "Milestones" },
  { id: "revenue", label: "Revenue" },
  { id: "model", label: "What Hi Power Does" },
  { id: "capabilities", label: "Core Competencies" },
  { id: "solutions", label: "Business Domains" },
  { id: "proof", label: "Proof" },
  { id: "global", label: "Global" },
  { id: "phison", label: "Phison Pitch" },
  { id: "team", label: "Team" },
  { id: "sections", label: "Section Sequence" },
  { id: "partnership", label: "Partnership" },
  { id: "contactForm", label: "Contact Form" },
  { id: "footer", label: "Footer" },
  { id: "messages", label: "Messages" },
  { id: "media", label: "Media & Pins" },
];

const sectionLabels = {
  hero: "Hero",
  snapshot: "Company Snapshot",
  milestones: "Milestones",
  lineCard: "Authorized Line Card",
  revenue: "Revenue Growth",
  trinityModel: "What Hi Power Actually Does",
  proof: "Project Proof Areas",
  capabilities: "Core Competencies",
  solutions: "Business Domains",
  global: "Global Presence",
  phison: "Phison Partnership Brief",
  team: "Representatives",
  partnership: "Partnership Opportunities",
  contactForm: "Contact Form",
  footer: "Footer",
};

const cloneContent = (value) => JSON.parse(JSON.stringify(value));

const cloneSeed = () => cloneContent(getEditableSeedContent());

const updateAtPath = (source, path, value) => {
  const next = cloneContent(source || cloneSeed());
  let cursor = next;
  path.slice(0, -1).forEach((key) => {
    if (cursor[key] === undefined || cursor[key] === null) cursor[key] = typeof key === "number" ? [] : {};
    cursor = cursor[key];
  });
  cursor[path[path.length - 1]] = value;
  return next;
};

const asCoordinateText = (coordinates = []) => coordinates.join(", ");

const parseCoordinateText = (text) => text.split(",").map((item) => Number(item.trim())).filter((item) => !Number.isNaN(item));

const moveItem = (items, index, direction) => {
  const next = [...items];
  const target = index + direction;
  if (target < 0 || target >= next.length) return next;
  [next[index], next[target]] = [next[target], next[index]];
  return next;
};

export default function AdminEditor() {
  const [email, setEmail] = useState("admin@trinity.local");
  const [password, setPassword] = useState("");
  const [token, setToken] = useState(localStorage.getItem("trinity_admin_token") || "");
  const [content, setContent] = useState(cloneSeed());
  const [message, setMessage] = useState("");
  const [language, setLanguage] = useState("en");
  const [activeTab, setActiveTab] = useState("hero");
  const [submissions, setSubmissions] = useState([]);
  const deckContent = useMemo(() => getLocalizedContentFromEditable(language, content), [language, content]);

  const handleDownloadPptx = () => {
    setMessage("Generating PowerPoint...");
    import("@/lib/exportPptx")
      .then((module) => module.exportDeckToPptx(deckContent))
      .then(() => setMessage("PowerPoint downloaded."))
      .catch(() => setMessage("Could not generate the PPT. Please check your connection and try again."));
  };

  useEffect(() => {
    if (!token) return;
    fetchAdminContent(token)
      .then((content) => {
        const editable = Object.keys(content || {}).length && content.version === CONTENT_VERSION ? normalizeEditableContent(content) : cloneSeed();
        setContent(editable);
      })
      .catch((error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem("trinity_admin_token");
          setToken("");
          setMessage("Your admin session expired. Please log in again.");
        } else {
          setMessage("Could not load saved content. Please login again.");
        }
      });
  }, [token]);

  useEffect(() => {
    if (!token || activeTab !== "messages") return;
    fetchContactSubmissions(token)
      .then(setSubmissions)
      .catch((error) => setMessage(error.response?.data?.detail || "Could not load messages."));
  }, [token, activeTab]);

  const current = content.translations?.[language] || content.translations?.en || cloneSeed().translations.en;

  const updatePath = (path, value) => setContent((previous) => updateAtPath(previous, path, value));

  const updateTranslation = (path, value) => updatePath(["translations", language, ...path], value);

  const handleLogin = async (event) => {
    event.preventDefault();
    setMessage("Logging in...");
    try {
      const data = await loginAdmin(email, password);
      localStorage.setItem("trinity_admin_token", data.token);
      setToken(data.token);
      setMessage("Logged in. You can now edit content with forms.");
    } catch (error) {
      setMessage(error.response?.data?.detail || "Login failed");
    }
  };

  const handleSave = async () => {
    setMessage("Saving...");
    try {
      await saveAdminContent(token, content);
      setMessage(`Saved successfully at ${new Date().toLocaleTimeString()}. Public page will use this content.`);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("trinity_admin_token");
        setToken("");
        setMessage("Your admin session expired. Please log in again, then save.");
      } else {
        setMessage(error.response?.data?.detail || error.message || "Save failed");
      }
    }
  };

  const handleResetSeed = () => {
    setContent(cloneSeed());
    setMessage("Loaded editable seed content. Save to publish it.");
  };

  const renderSite = () => (
    <AdminSection title="Site Navigation" eyebrow="Brand and header labels">
      <AdminField id="admin-site-brand" label="Brand Name" value={current.brand} onChange={(value) => updateTranslation(["brand"], value)} />
      <AdminField id="admin-site-contact-label" label="Header Contact Label" value={current.contact} onChange={(value) => updateTranslation(["contact"], value)} />
      <AdminListField id="admin-site-nav" label="Navigation Labels" values={current.nav} onChange={(value) => updateTranslation(["nav"], value)} />
      <AdminListField id="admin-site-model-flow" label="Engineering Flow Labels" values={current.modelFlow} onChange={(value) => updateTranslation(["modelFlow"], value)} />
      <AdminField id="admin-site-brand-logo" label="Brand Logo URL" value={content.common?.brandLogo || ""} onChange={(value) => updatePath(["common", "brandLogo"], value)} />
    </AdminSection>
  );

  const renderHero = () => (
    <AdminSection title="Hero" eyebrow="Homepage first section">
      <AdminField id="admin-hero-overline" label="Overline" value={current.hero?.overline} onChange={(value) => updateTranslation(["hero", "overline"], value)} />
      <AdminField id="admin-hero-company" label="Company Label" value={current.hero?.company} onChange={(value) => updateTranslation(["hero", "company"], value)} />
      <AdminField id="admin-hero-headline" label="Headline" value={current.hero?.headline} onChange={(value) => updateTranslation(["hero", "headline"], value)} />
      <AdminField id="admin-hero-subheadline" label="Subheadline" value={current.hero?.subheadline} onChange={(value) => updateTranslation(["hero", "subheadline"], value)} />
      <AdminField id="admin-hero-description" label="Description" value={current.hero?.description} multiline onChange={(value) => updateTranslation(["hero", "description"], value)} />
      <AdminListField id="admin-hero-domains" label="Domain Tags" values={current.hero?.domains} onChange={(value) => updateTranslation(["hero", "domains"], value)} />
      <AdminListField id="admin-hero-panel-labels" label="Image Panel Labels" values={current.hero?.panelLabels} onChange={(value) => updateTranslation(["hero", "panelLabels"], value)} />
      <AdminListField id="admin-hero-buttons" label="CTA Buttons" values={current.hero?.ctas} onChange={(value) => updateTranslation(["hero", "ctas"], value)} />
    </AdminSection>
  );

  const renderSnapshot = () => (
    <AdminSection title="Snapshot" eyebrow="Company facts">
      <AdminField id="admin-snapshot-overline" label="Overline" value={current.snapshot?.overline} onChange={(value) => updateTranslation(["snapshot", "overline"], value)} />
      <AdminField id="admin-snapshot-title" label="Title" value={current.snapshot?.title} onChange={(value) => updateTranslation(["snapshot", "title"], value)} />
      <AdminField id="admin-snapshot-description" label="Description" value={current.snapshot?.description} multiline onChange={(value) => updateTranslation(["snapshot", "description"], value)} />
      {(current.snapshot?.facts || []).map((fact, index) => (
        <div className="admin-nested-card" key={`${fact.label}-${index}`} data-testid={`admin-snapshot-fact-${index}-card`}>
          <AdminField id={`admin-snapshot-fact-${index}-label`} label={`Fact ${index + 1} Label`} value={fact.label} onChange={(value) => updateTranslation(["snapshot", "facts", index, "label"], value)} />
          <AdminField id={`admin-snapshot-fact-${index}-value`} label={`Fact ${index + 1} Value`} value={fact.value} onChange={(value) => updateTranslation(["snapshot", "facts", index, "value"], value)} />
        </div>
      ))}
    </AdminSection>
  );

  const renderLineCard = () => (
    <AdminSection title="Authorized Line Card" eyebrow="Products and applications">
      <AdminField id="admin-linecard-overline" label="Overline" value={current.lineCard?.overline} onChange={(value) => updateTranslation(["lineCard", "overline"], value)} />
      <AdminField id="admin-linecard-title" label="Title" value={current.lineCard?.title} onChange={(value) => updateTranslation(["lineCard", "title"], value)} />
      <AdminField id="admin-linecard-description" label="Description" value={current.lineCard?.description} multiline onChange={(value) => updateTranslation(["lineCard", "description"], value)} />
      <AdminField id="admin-linecard-products-label" label="Products Label" value={current.lineCard?.makesLabel} onChange={(value) => updateTranslation(["lineCard", "makesLabel"], value)} />
      <AdminField id="admin-linecard-apps-label" label="Applications Label" value={current.lineCard?.appsLabel} onChange={(value) => updateTranslation(["lineCard", "appsLabel"], value)} />
      {(current.lineCard?.partners || []).map((partner, index) => (
        <div className="admin-nested-card wide" key={`${partner.name}-${index}`} data-testid={`admin-linecard-partner-${index}-editor`}>
          <AdminField id={`admin-linecard-partner-${index}-name`} label={`Partner ${index + 1} Name`} value={partner.name} onChange={(value) => updateTranslation(["lineCard", "partners", index, "name"], value)} />
          <AdminField id={`admin-linecard-partner-${index}-native`} label="Native / Chinese Name" value={partner.native} onChange={(value) => updateTranslation(["lineCard", "partners", index, "native"], value)} />
          <AdminField id={`admin-linecard-partner-${index}-category`} label="Category" value={partner.category} onChange={(value) => updateTranslation(["lineCard", "partners", index, "category"], value)} />
          <AdminField id={`admin-linecard-partner-${index}-makes`} label="Products" value={partner.makes} multiline onChange={(value) => updateTranslation(["lineCard", "partners", index, "makes"], value)} />
          <AdminField id={`admin-linecard-partner-${index}-apps`} label="Applications" value={partner.apps} multiline onChange={(value) => updateTranslation(["lineCard", "partners", index, "apps"], value)} />
          <AdminField id={`admin-linecard-partner-${index}-logo`} label="Partner Logo URL" value={content.common?.partnerLogos?.[index] || ""} onChange={(value) => updatePath(["common", "partnerLogos", index], value)} />
        </div>
      ))}
    </AdminSection>
  );

  const renderMilestones = () => (
    <AdminSection title="Milestones" eyebrow="Company timeline">
      <AdminField id="admin-milestones-overline" label="Overline" value={current.milestones?.overline} onChange={(value) => updateTranslation(["milestones", "overline"], value)} />
      <AdminField id="admin-milestones-title" label="Title" value={current.milestones?.title} onChange={(value) => updateTranslation(["milestones", "title"], value)} />
      <AdminField id="admin-milestones-description" label="Description" value={current.milestones?.description} multiline onChange={(value) => updateTranslation(["milestones", "description"], value)} />
      {(current.milestones?.items || []).map((item, index) => (
        <div className="admin-nested-card wide" key={`${item.year}-${index}`} data-testid={`admin-milestone-${index}-editor`}>
          <AdminField id={`admin-milestone-${index}-year`} label={`Milestone ${index + 1} Year`} value={item.year} onChange={(value) => updateTranslation(["milestones", "items", index, "year"], value)} />
          <AdminField id={`admin-milestone-${index}-title`} label="Title" value={item.title} onChange={(value) => updateTranslation(["milestones", "items", index, "title"], value)} />
          <AdminField id={`admin-milestone-${index}-text`} label="Text" value={item.text} multiline onChange={(value) => updateTranslation(["milestones", "items", index, "text"], value)} />
        </div>
      ))}
    </AdminSection>
  );

  const renderProof = () => (
    <AdminSection title="Proof" eyebrow="Project proof areas">
      <AdminField id="admin-proof-overline" label="Overline" value={current.proof?.overline} onChange={(value) => updateTranslation(["proof", "overline"], value)} />
      <AdminField id="admin-proof-title" label="Title" value={current.proof?.title} onChange={(value) => updateTranslation(["proof", "title"], value)} />
      <AdminField id="admin-proof-description" label="Description" value={current.proof?.description} multiline onChange={(value) => updateTranslation(["proof", "description"], value)} />
      {(current.proof?.stats || []).map((stat, index) => (
        <div className="admin-nested-card compact" key={`${stat.value}-${index}`} data-testid={`admin-proof-stat-${index}-card`}>
          <AdminField id={`admin-proof-stat-${index}-value`} label={`Stat ${index + 1} Value`} value={stat.value} onChange={(value) => updateTranslation(["proof", "stats", index, "value"], value)} />
          <AdminField id={`admin-proof-stat-${index}-label`} label={`Stat ${index + 1} Label`} value={stat.label} onChange={(value) => updateTranslation(["proof", "stats", index, "label"], value)} />
        </div>
      ))}
      {(current.proof?.cards || []).map((card, index) => (
        <div className="admin-nested-card wide" key={`${card.title}-${index}`} data-testid={`admin-proof-card-${index}-editor`}>
          <AdminField id={`admin-proof-card-${index}-title`} label={`Proof Card ${index + 1} Title`} value={card.title} onChange={(value) => updateTranslation(["proof", "cards", index, "title"], value)} />
          <AdminField id={`admin-proof-card-${index}-category`} label="Category" value={card.category} onChange={(value) => updateTranslation(["proof", "cards", index, "category"], value)} />
          <AdminField id={`admin-proof-card-${index}-image`} label="Case Study Image URL" value={content.common?.proofImages?.[index]?.image} onChange={(value) => updatePath(["common", "proofImages", index, "image"], value)} />
          <AdminListField id={`admin-proof-card-${index}-scope`} label="Scope Items" values={card.scope} onChange={(value) => updateTranslation(["proof", "cards", index, "scope"], value)} />
          <AdminField id={`admin-proof-card-${index}-outcome`} label="Outcome" value={card.outcome} multiline onChange={(value) => updateTranslation(["proof", "cards", index, "outcome"], value)} />
        </div>
      ))}
    </AdminSection>
  );

  const renderRevenue = () => (
    <AdminSection title="Revenue" eyebrow="Commercial growth">
      <AdminField id="admin-revenue-overline" label="Overline" value={current.revenue?.overline} onChange={(value) => updateTranslation(["revenue", "overline"], value)} />
      <AdminField id="admin-revenue-title" label="Title" value={current.revenue?.title} onChange={(value) => updateTranslation(["revenue", "title"], value)} />
      <AdminField id="admin-revenue-description" label="Description" value={current.revenue?.description} multiline onChange={(value) => updateTranslation(["revenue", "description"], value)} />
      {(current.revenue?.items || []).map((item, index) => (
        <div className="admin-nested-card" key={`${item.year}-${index}`} data-testid={`admin-revenue-item-${index}-editor`}>
          <AdminField id={`admin-revenue-item-${index}-year`} label={`Revenue ${index + 1} Year`} value={item.year} onChange={(value) => updateTranslation(["revenue", "items", index, "year"], value)} />
          <AdminField id={`admin-revenue-item-${index}-value`} label="Value" value={item.value} onChange={(value) => updateTranslation(["revenue", "items", index, "value"], value)} />
          <AdminField id={`admin-revenue-item-${index}-label`} label="Label" value={item.label} onChange={(value) => updateTranslation(["revenue", "items", index, "label"], value)} />
        </div>
      ))}
    </AdminSection>
  );

  const renderModel = () => (
    <AdminSection title="What Hi Power Does" eyebrow="Operating model and positioning">
      <AdminField id="admin-model-overline" label="Overline" value={current.operatingModel?.overline} onChange={(value) => updateTranslation(["operatingModel", "overline"], value)} />
      <AdminField id="admin-model-title" label="Title" value={current.operatingModel?.title} onChange={(value) => updateTranslation(["operatingModel", "title"], value)} />
      <AdminField id="admin-model-description" label="Description" value={current.operatingModel?.description} multiline onChange={(value) => updateTranslation(["operatingModel", "description"], value)} />
      {(current.operatingModel?.cards || []).map((card, index) => (
        <div className="admin-nested-card wide" key={`${card.title}-${index}`} data-testid={`admin-model-card-${index}-editor`}>
          <AdminField id={`admin-model-card-${index}-title`} label={`Model Card ${index + 1} Title`} value={card.title} onChange={(value) => updateTranslation(["operatingModel", "cards", index, "title"], value)} />
          <AdminField id={`admin-model-card-${index}-text`} label="Text" value={card.text} multiline onChange={(value) => updateTranslation(["operatingModel", "cards", index, "text"], value)} />
          <AdminListField id={`admin-model-card-${index}-items`} label="Items" values={card.items} onChange={(value) => updateTranslation(["operatingModel", "cards", index, "items"], value)} />
        </div>
      ))}
      <AdminField id="admin-positioning-overline" label="Positioning Overline" value={current.positioning?.overline} onChange={(value) => updateTranslation(["positioning", "overline"], value)} />
      <AdminField id="admin-positioning-title" label="Positioning Title" value={current.positioning?.title} onChange={(value) => updateTranslation(["positioning", "title"], value)} />
      <AdminField id="admin-positioning-body" label="Positioning Body" value={current.positioning?.body} multiline onChange={(value) => updateTranslation(["positioning", "body"], value)} />
      {(current.positioning?.cards || []).map(([title, text], index) => (
        <div className="admin-nested-card" key={`${title}-${index}`} data-testid={`admin-positioning-card-${index}-editor`}>
          <AdminField id={`admin-positioning-card-${index}-title`} label={`Positioning ${index + 1} Title`} value={title} onChange={(value) => updateTranslation(["positioning", "cards", index, 0], value)} />
          <AdminField id={`admin-positioning-card-${index}-text`} label="Text" value={text} multiline onChange={(value) => updateTranslation(["positioning", "cards", index, 1], value)} />
        </div>
      ))}
    </AdminSection>
  );

  const renderCapabilities = () => (
    <AdminSection title="Core Competencies" eyebrow="Technical and commercial capabilities">
      <AdminField id="admin-capabilities-overline" label="Overline" value={current.capabilities?.overline} onChange={(value) => updateTranslation(["capabilities", "overline"], value)} />
      <AdminField id="admin-capabilities-title" label="Title" value={current.capabilities?.title} onChange={(value) => updateTranslation(["capabilities", "title"], value)} />
      <AdminField id="admin-capabilities-thesis" label="Thesis" value={current.capabilities?.thesis} multiline onChange={(value) => updateTranslation(["capabilities", "thesis"], value)} />
      <AdminField id="admin-camera-overline" label="Camera Expertise Overline" value={current.cameraExpertise?.overline} onChange={(value) => updateTranslation(["cameraExpertise", "overline"], value)} />
      <AdminField id="admin-camera-title" label="Camera Expertise Title" value={current.cameraExpertise?.title} onChange={(value) => updateTranslation(["cameraExpertise", "title"], value)} />
      <AdminField id="admin-camera-description" label="Section Description" value={current.cameraExpertise?.description} multiline onChange={(value) => updateTranslation(["cameraExpertise", "description"], value)} />
      {(current.capabilities?.columns || []).map((column, index) => (
        <div className="admin-nested-card" key={`${column.title}-${index}`} data-testid={`admin-capability-column-${index}-editor`}>
          <AdminField id={`admin-capability-column-${index}-title`} label={`Column ${index + 1} Title`} value={column.title} onChange={(value) => updateTranslation(["capabilities", "columns", index, "title"], value)} />
          <AdminListField id={`admin-capability-column-${index}-items`} label="Items" values={column.items} onChange={(value) => updateTranslation(["capabilities", "columns", index, "items"], value)} />
        </div>
      ))}
      <AdminField id="admin-camera-activity-number" label="Camera Activity Number" value={current.cameraExpertise?.activity?.number} onChange={(value) => updateTranslation(["cameraExpertise", "activity", "number"], value)} />
      <AdminField id="admin-camera-activity-title" label="Camera Activity Title" value={current.cameraExpertise?.activity?.title} onChange={(value) => updateTranslation(["cameraExpertise", "activity", "title"], value)} />
      <AdminField id="admin-camera-activity-text" label="Camera Activity Text" value={current.cameraExpertise?.activity?.text} multiline onChange={(value) => updateTranslation(["cameraExpertise", "activity", "text"], value)} />
      <AdminField id="admin-camera-iq-eyebrow" label="IQ Eyebrow" value={current.cameraExpertise?.iq?.eyebrow} onChange={(value) => updateTranslation(["cameraExpertise", "iq", "eyebrow"], value)} />
      <AdminField id="admin-camera-iq-title" label="IQ Title" value={current.cameraExpertise?.iq?.title} onChange={(value) => updateTranslation(["cameraExpertise", "iq", "title"], value)} />
      <AdminField id="admin-camera-iq-text" label="Camera Activity Text" value={current.cameraExpertise?.iq?.text} multiline onChange={(value) => updateTranslation(["cameraExpertise", "iq", "text"], value)} />
    </AdminSection>
  );

  const renderSolutions = () => (
    <AdminSection title="Business Domains" eyebrow="Solution cards">
      <AdminField id="admin-solutions-overline" label="Overline" value={current.solutions?.overline} onChange={(value) => updateTranslation(["solutions", "overline"], value)} />
      <AdminField id="admin-solutions-title" label="Title" value={current.solutions?.title} onChange={(value) => updateTranslation(["solutions", "title"], value)} />
      {(current.solutions?.domains || []).map((domain, index) => (
        <div className="admin-nested-card wide" key={`${domain.title}-${index}`} data-testid={`admin-solution-domain-${index}-editor`}>
          <AdminField id={`admin-solution-domain-${index}-title`} label={`Domain ${index + 1} Title`} value={domain.title} onChange={(value) => updateTranslation(["solutions", "domains", index, "title"], value)} />
          <AdminField id={`admin-solution-domain-${index}-eyebrow`} label="Eyebrow" value={domain.eyebrow} onChange={(value) => updateTranslation(["solutions", "domains", index, "eyebrow"], value)} />
          <AdminField id={`admin-solution-domain-${index}-description`} label="Description" value={domain.description} multiline onChange={(value) => updateTranslation(["solutions", "domains", index, "description"], value)} />
          <AdminListField id={`admin-solution-domain-${index}-items`} label="Feature Items" values={domain.items} onChange={(value) => updateTranslation(["solutions", "domains", index, "items"], value)} />
          <AdminListField id={`admin-solution-domain-${index}-products`} label="Product Tags" values={domain.products} onChange={(value) => updateTranslation(["solutions", "domains", index, "products"], value)} />
        </div>
      ))}
    </AdminSection>
  );

  const renderGlobal = () => (
    <AdminSection title="Global" eyebrow="Markets and map text">
      <AdminField id="admin-global-overline" label="Overline" value={current.global?.overline} onChange={(value) => updateTranslation(["global", "overline"], value)} />
      <AdminField id="admin-global-title" label="Title" value={current.global?.title} onChange={(value) => updateTranslation(["global", "title"], value)} />
      <AdminListField id="admin-global-regions" label="Region Labels" values={current.global?.regions} onChange={(value) => updateTranslation(["global", "regions"], value)} />
      <AdminListField id="admin-global-stats" label="Global Stats" values={current.global?.stats} onChange={(value) => updateTranslation(["global", "stats"], value)} />
    </AdminSection>
  );

  const renderPhison = () => (
    <AdminSection title="Phison Pitch" eyebrow="Partner-specific section">
      <AdminField id="admin-phison-overline" label="Overline" value={current.phison?.overline} onChange={(value) => updateTranslation(["phison", "overline"], value)} />
      <AdminField id="admin-phison-title" label="Title" value={current.phison?.title} onChange={(value) => updateTranslation(["phison", "title"], value)} />
      <AdminField id="admin-phison-description" label="Description" value={current.phison?.description} multiline onChange={(value) => updateTranslation(["phison", "description"], value)} />
      {(current.phison?.points || []).map((point, index) => (
        <div className="admin-nested-card" key={`${point.title}-${index}`} data-testid={`admin-phison-point-${index}-editor`}>
          <AdminField id={`admin-phison-point-${index}-title`} label={`Point ${index + 1} Title`} value={point.title} onChange={(value) => updateTranslation(["phison", "points", index, "title"], value)} />
          <AdminField id={`admin-phison-point-${index}-text`} label="Text" value={point.text} multiline onChange={(value) => updateTranslation(["phison", "points", index, "text"], value)} />
        </div>
      ))}
      <AdminListField id="admin-phison-stats" label="Stat Chips" values={current.phison?.stats} onChange={(value) => updateTranslation(["phison", "stats"], value)} />
    </AdminSection>
  );

  const renderPartnership = () => (
    <AdminSection title="Partnership" eyebrow="Final call to action">
      <AdminField id="admin-partnership-overline" label="Overline" value={current.partnership?.overline} onChange={(value) => updateTranslation(["partnership", "overline"], value)} />
      <AdminField id="admin-partnership-title" label="Title" value={current.partnership?.title} onChange={(value) => updateTranslation(["partnership", "title"], value)} />
      <AdminField id="admin-partnership-description" label="Description" value={current.partnership?.description} multiline onChange={(value) => updateTranslation(["partnership", "description"], value)} />
      <AdminListField id="admin-partnership-buttons" label="CTA Buttons" values={current.partnership?.buttons} onChange={(value) => updateTranslation(["partnership", "buttons"], value)} />
      <p className="admin-note" data-testid="admin-partnership-note">Partner chips were removed from the public page.</p>
    </AdminSection>
  );

  const renderTeam = () => (
    <AdminSection title="Team" eyebrow="Engineering and FAE support">
      <AdminField id="admin-team-overline" label="Overline" value={current.team?.overline} onChange={(value) => updateTranslation(["team", "overline"], value)} />
      <AdminField id="admin-team-title" label="Title" value={current.team?.title} onChange={(value) => updateTranslation(["team", "title"], value)} />
      <AdminField id="admin-team-photo-label" label="Photo Placeholder Label" value={current.team?.photo} onChange={(value) => updateTranslation(["team", "photo"], value)} />
      {(current.team?.teams || []).map(([region, representative, coverage], index) => (
        <div className="admin-nested-card wide" key={`${region}-${index}`} data-testid={`admin-team-member-${index}-editor`}>
          <AdminField id={`admin-team-member-${index}-region`} label={`Team ${index + 1} Area`} value={region} onChange={(value) => updateTranslation(["team", "teams", index, 0], value)} />
          <AdminField id={`admin-team-member-${index}-representative`} label="Representative / Role" value={representative} onChange={(value) => updateTranslation(["team", "teams", index, 1], value)} />
          <AdminListField id={`admin-team-member-${index}-coverage`} label="Coverage Items" values={coverage} onChange={(value) => updateTranslation(["team", "teams", index, 2], value)} />
          <AdminField id={`admin-team-member-${index}-image`} label="Team Image URL" value={content.common?.teamImages?.[index]?.image || ""} onChange={(value) => updatePath(["common", "teamImages", index, "image"], value)} />
        </div>
      ))}
    </AdminSection>
  );

  const renderContactForm = () => (
    <AdminSection title="Contact Form" eyebrow="Inquiry form copy and labels">
      <AdminField id="admin-contact-form-overline" label="Overline" value={current.contactForm?.overline} onChange={(value) => updateTranslation(["contactForm", "overline"], value)} />
      <AdminField id="admin-contact-form-title" label="Title" value={current.contactForm?.title} onChange={(value) => updateTranslation(["contactForm", "title"], value)} />
      <AdminField id="admin-contact-form-description" label="Description" value={current.contactForm?.description} multiline onChange={(value) => updateTranslation(["contactForm", "description"], value)} />
      {Object.entries(current.contactForm?.fields || {}).map(([fieldKey, label]) => (
        <AdminField key={fieldKey} id={`admin-contact-form-field-${fieldKey}`} label={`${fieldKey} Field Label`} value={label} onChange={(value) => updateTranslation(["contactForm", "fields", fieldKey], value)} />
      ))}
      <AdminField id="admin-contact-form-submit" label="Submit Label" value={current.contactForm?.submitLabel} onChange={(value) => updateTranslation(["contactForm", "submitLabel"], value)} />
      <AdminField id="admin-contact-form-sending" label="Sending Label" value={current.contactForm?.sendingLabel} onChange={(value) => updateTranslation(["contactForm", "sendingLabel"], value)} />
      <AdminField id="admin-contact-form-success" label="Success Label" value={current.contactForm?.successLabel} onChange={(value) => updateTranslation(["contactForm", "successLabel"], value)} />
      <AdminField id="admin-contact-form-saved" label="Saved Label" value={current.contactForm?.savedLabel} onChange={(value) => updateTranslation(["contactForm", "savedLabel"], value)} />
      <AdminField id="admin-contact-form-error" label="Error Label" value={current.contactForm?.errorLabel} onChange={(value) => updateTranslation(["contactForm", "errorLabel"], value)} />
    </AdminSection>
  );

  const renderFooter = () => (
    <AdminSection title="Footer Contact" eyebrow="Editable contact details">
      <AdminField id="admin-footer-company" label="Company Name" value={current.footer?.company} onChange={(value) => updateTranslation(["footer", "company"], value)} />
      <AdminField id="admin-footer-address" label="Address" value={current.footer?.address} multiline onChange={(value) => updateTranslation(["footer", "address"], value)} />
      <AdminField id="admin-footer-phone" label="Phone" value={current.footer?.phone} onChange={(value) => updateTranslation(["footer", "phone"], value)} />
      <AdminField id="admin-footer-email" label="Email" value={current.footer?.email} onChange={(value) => updateTranslation(["footer", "email"], value)} />
      <AdminField id="admin-footer-copyright" label="Copyright Line" value={current.footer?.copyright} multiline onChange={(value) => updateTranslation(["footer", "copyright"], value)} />
    </AdminSection>
  );

  const renderMessages = () => (
    <AdminSection title="Visitor Messages" eyebrow="Website inquiry inbox">
      <div className="admin-message-list" data-testid="admin-message-list">
        {submissions.length === 0 ? <p className="admin-help" data-testid="admin-no-messages">No messages yet.</p> : null}
        {submissions.map((submission) => (
          <article className="admin-message-card" key={submission.id} data-testid={`admin-message-${submission.id}`}>
            <div>
              <strong data-testid={`admin-message-name-${submission.id}`}>{submission.name}</strong>
              <span data-testid={`admin-message-date-${submission.id}`}>{new Date(submission.created_at).toLocaleString()}</span>
            </div>
            <p data-testid={`admin-message-contact-${submission.id}`}>{submission.email}{submission.company ? ` · ${submission.company}` : ""}{submission.phone ? ` · ${submission.phone}` : ""}</p>
            {submission.interest ? <p data-testid={`admin-message-interest-${submission.id}`}>{submission.interest}</p> : null}
            <p data-testid={`admin-message-body-${submission.id}`}>{submission.message}</p>
            <em data-testid={`admin-message-status-${submission.id}`}>{submission.status}</em>
          </article>
        ))}
      </div>
    </AdminSection>
  );

  const renderMedia = () => (
    <AdminSection title="Media & Pins" eyebrow="Images and map coordinates">
      {(content.common?.heroPanels || []).map((panel, index) => (
        <AdminField key={`panel-${index}`} id={`admin-hero-panel-${index}-image`} label={`Hero Panel ${index + 1} Image URL`} value={panel.image} onChange={(value) => updatePath(["common", "heroPanels", index, "image"], value)} />
      ))}
      {(content.common?.snapshotImages || []).map((image, index) => (
        <AdminField key={`snapshot-image-${index}`} id={`admin-snapshot-media-${index}-image`} label={`Company Snapshot Slide ${index + 1} Image URL`} value={image.image} onChange={(value) => updatePath(["common", "snapshotImages", index, "image"], value)} />
      ))}
      {(content.common?.lineCardImages || []).map((image, index) => (
        <AdminField key={`linecard-image-${index}`} id={`admin-linecard-media-${index}-image`} label={`Line Card Slide ${index + 1} Image URL`} value={image.image} onChange={(value) => updatePath(["common", "lineCardImages", index, "image"], value)} />
      ))}
      {(content.common?.milestoneImages || []).map((image, index) => (
        <AdminField key={`milestone-image-${index}`} id={`admin-milestone-media-${index}-image`} label={`Milestones Slide ${index + 1} Image URL`} value={image.image} onChange={(value) => updatePath(["common", "milestoneImages", index, "image"], value)} />
      ))}
      {(content.common?.phisonImages || []).map((image, index) => (
        <AdminField key={`phison-image-${index}`} id={`admin-phison-media-${index}-image`} label={`Phison Slide ${index + 1} Image URL`} value={image.image} onChange={(value) => updatePath(["common", "phisonImages", index, "image"], value)} />
      ))}
      {(content.common?.capabilityImages || []).map((image, index) => (
        <AdminField key={`capability-image-${index}`} id={`admin-capability-media-${index}-image`} label={`Capabilities Slide ${index + 1} Image URL`} value={image.image} onChange={(value) => updatePath(["common", "capabilityImages", index, "image"], value)} />
      ))}
      {(content.common?.proofImages || []).map((image, index) => (
        <AdminField key={`proof-image-${index}`} id={`admin-proof-media-${index}-image`} label={`Project Proof Area ${index + 1} Image URL`} value={image.image} onChange={(value) => updatePath(["common", "proofImages", index, "image"], value)} />
      ))}
      {(content.common?.teamImages || []).map((image, index) => (
        <AdminField key={`team-image-${index}`} id={`admin-team-media-${index}-image`} label={`Representative ${index + 1} Image URL`} value={image.image} onChange={(value) => updatePath(["common", "teamImages", index, "image"], value)} />
      ))}
      <AdminField id="admin-brand-logo-image" label="Brand Logo URL (header)" value={content.common?.brandLogo || ""} onChange={(value) => updatePath(["common", "brandLogo"], value)} />
      {(content.common?.partnerLogos || []).map((logo, index) => (
        <AdminField key={`partner-logo-${index}`} id={`admin-partner-logo-${index}-image`} label={`Line Card Logo ${index + 1} URL`} value={logo} onChange={(value) => updatePath(["common", "partnerLogos", index], value)} />
      ))}
      {(content.common?.solutionImages || []).map((image, index) => (
        <AdminField key={`solution-image-${index}`} id={`admin-solution-media-${index}-image`} label={`Solution ${index + 1} Image URL`} value={image} onChange={(value) => updatePath(["common", "solutionImages", index], value)} />
      ))}
      {(content.common?.globalImages || []).map((image, index) => (
        <AdminField key={`global-image-${index}`} id={`admin-global-media-${index}-image`} label={`Global Slide ${index + 1} Image URL`} value={image.image} onChange={(value) => updatePath(["common", "globalImages", index, "image"], value)} />
      ))}
      {(content.common?.partnershipImages || []).map((image, index) => (
        <AdminField key={`partnership-image-${index}`} id={`admin-partnership-media-${index}-image`} label={`Partnership CTA Slide ${index + 1} Image URL`} value={image.image} onChange={(value) => updatePath(["common", "partnershipImages", index, "image"], value)} />
      ))}
      {(content.common?.mapRegions || []).map((region, index) => (
        <div className="admin-nested-card compact" key={region.key} data-testid={`admin-map-region-${region.key}-card`}>
          <AdminField id={`admin-map-region-${index}-key`} label="Region Key" value={region.key} onChange={(value) => updatePath(["common", "mapRegions", index, "key"], value)} />
          <AdminField id={`admin-map-region-${index}-coordinates`} label="Coordinates: longitude, latitude" value={asCoordinateText(region.coordinates)} onChange={(value) => updatePath(["common", "mapRegions", index, "coordinates"], parseCoordinateText(value))} />
          <AdminField id={`admin-map-region-${index}-group`} label="Region Group" value={region.group} onChange={(value) => updatePath(["common", "mapRegions", index, "group"], value)} />
        </div>
      ))}
    </AdminSection>
  );

  const renderSections = () => (
    <AdminSection title="Section Sequence" eyebrow="Homepage order controls">
      <p className="admin-note" data-testid="admin-section-hide-note">Use Hide to remove a section from the landing page without deleting its content.</p>
      <div className="admin-sequence-list" data-testid="admin-section-sequence-list">
        {(content.common?.sectionOrder || []).map((sectionKey, index) => (
          <div className="admin-sequence-item" key={sectionKey} data-testid={`admin-section-sequence-${sectionKey}`}>
            <span data-testid={`admin-section-sequence-label-${sectionKey}`}>{String(index + 1).padStart(2, "0")} · {sectionLabels[sectionKey] || sectionKey}</span>
            <div className="admin-sequence-actions" data-testid={`admin-section-sequence-actions-${sectionKey}`}>
              <button
                type="button"
                className={(content.common.hiddenSections || []).includes(sectionKey) ? "active" : ""}
                onClick={() => {
                  const hidden = content.common.hiddenSections || [];
                  updatePath(["common", "hiddenSections"], hidden.includes(sectionKey) ? hidden.filter((item) => item !== sectionKey) : [...hidden, sectionKey]);
                }}
                data-testid={`admin-section-${sectionKey}-hide-toggle-button`}
              >
                {(content.common.hiddenSections || []).includes(sectionKey) ? "Show" : "Hide"}
              </button>
              <button type="button" onClick={() => updatePath(["common", "sectionOrder"], moveItem(content.common.sectionOrder, index, -1))} data-testid={`admin-section-${sectionKey}-move-up-button`}>Up</button>
              <button type="button" onClick={() => updatePath(["common", "sectionOrder"], moveItem(content.common.sectionOrder, index, 1))} data-testid={`admin-section-${sectionKey}-move-down-button`}>Down</button>
            </div>
          </div>
        ))}
      </div>
      <div className="admin-nested-card wide" data-testid="admin-standalone-sections-card">
        <p className="admin-note" data-testid="admin-standalone-sections-note">Contact Form and Footer are fixed-position page areas; use these toggles to show or hide them.</p>
        {["contactForm", "footer"].map((sectionKey) => (
          <div className="admin-sequence-item" key={sectionKey} data-testid={`admin-section-sequence-${sectionKey}`}>
            <span data-testid={`admin-section-sequence-label-${sectionKey}`}>{sectionLabels[sectionKey]}</span>
            <div className="admin-sequence-actions" data-testid={`admin-section-sequence-actions-${sectionKey}`}>
              <button
                type="button"
                className={(content.common.hiddenSections || []).includes(sectionKey) ? "active" : ""}
                onClick={() => {
                  const hidden = content.common.hiddenSections || [];
                  updatePath(["common", "hiddenSections"], hidden.includes(sectionKey) ? hidden.filter((item) => item !== sectionKey) : [...hidden, sectionKey]);
                }}
                data-testid={`admin-section-${sectionKey}-hide-toggle-button`}
              >
                {(content.common.hiddenSections || []).includes(sectionKey) ? "Show" : "Hide"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </AdminSection>
  );

  const renderActiveTab = () => {
    if (activeTab === "site") return renderSite();
    if (activeTab === "snapshot") return renderSnapshot();
    if (activeTab === "lineCard") return renderLineCard();
    if (activeTab === "milestones") return renderMilestones();
    if (activeTab === "revenue") return renderRevenue();
    if (activeTab === "model") return renderModel();
    if (activeTab === "capabilities") return renderCapabilities();
    if (activeTab === "solutions") return renderSolutions();
    if (activeTab === "proof") return renderProof();
    if (activeTab === "global") return renderGlobal();
    if (activeTab === "phison") return renderPhison();
    if (activeTab === "team") return renderTeam();
    if (activeTab === "sections") return renderSections();
    if (activeTab === "partnership") return renderPartnership();
    if (activeTab === "contactForm") return renderContactForm();
    if (activeTab === "footer") return renderFooter();
    if (activeTab === "messages") return renderMessages();
    if (activeTab === "media") return renderMedia();
    return renderHero();
  };

  if (!token) {
    return (
      <main className="admin-page" data-testid="admin-login-page">
        <form className="admin-card" onSubmit={handleLogin} data-testid="admin-login-form">
          <p className="overline" data-testid="admin-login-overline">Hi Power Content Editor</p>
          <h1 data-testid="admin-login-title">Admin Login</h1>
          <label data-testid="admin-email-label">Email</label>
          <input value={email} onChange={(event) => setEmail(event.target.value)} data-testid="admin-email-input" />
          <label data-testid="admin-password-label">Password</label>
          <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} data-testid="admin-password-input" />
          <button type="submit" data-testid="admin-login-button">Login</button>
          <p data-testid="admin-login-message">{message}</p>
        </form>
      </main>
    );
  }

  return (
    <main className="admin-page editor" data-testid="admin-editor-page">
      <div className="admin-toolbar" data-testid="admin-toolbar">
        <div>
          <p className="overline" data-testid="admin-editor-overline">Hi Power Content Editor</p>
          <h1 data-testid="admin-editor-title">Form-Based Content Editor</h1>
        </div>
        <div className="admin-actions">
          <button type="button" onClick={handleDownloadPptx} data-testid="admin-download-ppt-button">Download PPT</button>
          <button type="button" onClick={handleResetSeed} data-testid="admin-load-seed-button">Load Seed</button>
          <button type="button" onClick={handleSave} data-testid="admin-save-button">Save</button>
          <button type="button" onClick={() => { localStorage.removeItem("trinity_admin_token"); setToken(""); }} data-testid="admin-logout-button">Logout</button>
        </div>
      </div>
      <p className="admin-help" data-testid="admin-help-text">
        Select a language, edit structured fields, then save. Image URLs and map coordinates are shared across languages.
      </p>
      {message && (
        <p className="admin-status" role="status" data-testid="admin-editor-message">{message}</p>
      )}
      <div className="admin-editor-shell" data-testid="admin-editor-shell">
        <aside className="admin-sidebar" data-testid="admin-sidebar">
          <div className="admin-language-panel" data-testid="admin-language-panel">
            {languages.map(({ code }) => (
              <button
                type="button"
                key={code}
                className={language === code ? "active" : ""}
                onClick={() => setLanguage(code)}
                data-testid={`admin-language-${code}-button`}
              >
                {code.toUpperCase()}
              </button>
            ))}
          </div>
          <nav className="admin-tab-list" data-testid="admin-tab-list">
            {editorTabs.map((tab) => (
              <button
                type="button"
                key={tab.id}
                className={activeTab === tab.id ? "active" : ""}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`admin-tab-${tab.id}-button`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </aside>
        <div className="admin-form-area" data-testid="admin-form-area">
          {renderActiveTab()}
        </div>
      </div>
    </main>
  );
}